import { useId, useMemo } from 'react';
import { Card } from '@shared/ui/containers/Card';
import type { UsageSeriesPoint } from './api';
import { bucketDailyDeltas } from './utilityUsageMath';
import type { UtilityChartKind } from './utilityPresentation';

export type UtilityChartSize = 'compact' | 'full';

interface UtilityUsageChartProps {
  /** Fixed daily values (home widget). */
  data?: number[];
  /** Day labels under compact chart. */
  labels?: string[];
  /** Time-series for full/detail chart. */
  series?: UsageSeriesPoint[];
  registerCode?: string;
  mode?: 'cumulative' | 'delta';
  variant: UtilityChartKind;
  color: string;
  size?: UtilityChartSize;
  height?: number;
  emptyMessage?: string;
  /** Anchor day buckets to API window end (ISO string). */
  referenceDateIso?: string | null;
}

const COMPACT = { w: 232, h: 64 };
const FULL = { w: 320, h: 180 };

/**
 * Unified inline-SVG utility chart — area or bar, compact (home) or full (detail).
 */
export function UtilityUsageChart({
  data,
  labels,
  series,
  registerCode,
  mode = 'delta',
  variant,
  color,
  size = 'compact',
  height,
  emptyMessage = 'No readings yet.',
  referenceDateIso,
}: UtilityUsageChartProps): JSX.Element {
  const gradId = useId().replace(/:/g, '');

  const chartData = useMemo(() => {
    if (data && data.length > 0) {
      return {
        values: data,
        xLabels: labels ?? [],
        isTimeSeries: false as const,
      };
    }
    if (series && series.length > 0) {
      if (size === 'compact' && mode === 'delta') {
        const referenceMs = (() => {
          if (!referenceDateIso) return Date.now();
          const ms = new Date(referenceDateIso).getTime();
          return Number.isFinite(ms) ? ms : Date.now();
        })();
        const buckets = bucketDailyDeltas(series, registerCode, 7, referenceMs);
        return {
          values: buckets.map((b) => b.value),
          xLabels: buckets.map((b) => b.label),
          isTimeSeries: false as const,
        };
      }
      const filtered = registerCode
        ? series.filter((p) => p.register_code === registerCode)
        : series;
      const points =
        mode === 'delta'
          ? filtered.map((p) => ({
              at: new Date(p.at).getTime(),
              value: p.delta && p.delta > 0 ? p.delta : 0,
              estimate: p.is_estimate,
            }))
          : filtered.map((p) => ({
              at: new Date(p.at).getTime(),
              value: p.value,
              estimate: p.is_estimate,
            }));
      return { points, isTimeSeries: true as const };
    }
    return null;
  }, [data, labels, series, registerCode, mode, size, referenceDateIso]);

  if (!chartData) {
    if (size === 'full') {
      return (
        <Card noGrain className="flex h-44 w-full items-center justify-center rounded-lg p-0 shadow-none text-body-sm text-ink-muted">
          {emptyMessage}
        </Card>
      );
    }
    return (
      <div className="flex h-16 w-full items-center justify-center text-caption text-ink-muted">
        {emptyMessage}
      </div>
    );
  }

  if (chartData.isTimeSeries) {
    return (
      <TimeSeriesChart
        points={chartData.points}
        variant={variant}
        color={color}
        gradId={gradId}
        height={height ?? (size === 'compact' ? COMPACT.h : FULL.h)}
        width={size === 'compact' ? COMPACT.w : FULL.w}
        preserveAspectRatio={size === 'compact' ? 'xMidYMid meet' : 'none'}
        className={size === 'compact' ? 'h-16 w-full' : 'h-44 w-full'}
      />
    );
  }

  const values = chartData.values;
  if (values.every((v) => v === 0)) {
    if (size === 'full') {
      return (
        <Card noGrain className="flex h-44 w-full items-center justify-center rounded-lg p-0 shadow-none text-body-sm text-ink-muted">
          {emptyMessage}
        </Card>
      );
    }
    return (
      <div className="flex h-16 w-full items-center justify-center text-caption text-ink-muted">
        {emptyMessage}
      </div>
    );
  }

  const dims = size === 'compact' ? COMPACT : { w: FULL.w, h: height ?? FULL.h };
  const preserveAspectRatio = size === 'compact' ? 'xMidYMid meet' : 'none';
  const className = size === 'compact' ? 'h-16 w-full' : 'h-44 w-full';

  return (
    <div className="w-full">
      {variant === 'bar' ? (
        <BarChart
          values={values}
          color={color}
          width={dims.w}
          height={dims.h}
          preserveAspectRatio={preserveAspectRatio}
          className={className}
        />
      ) : (
        <AreaChart
          values={values}
          color={color}
          gradId={gradId}
          width={dims.w}
          height={dims.h}
          preserveAspectRatio={preserveAspectRatio}
          className={className}
          smooth={variant === 'line'}
        />
      )}
      {chartData.xLabels.length > 0 ? (
        <div
          className={
            size === 'compact'
              ? 'mt-1 flex justify-between px-0.5 text-[0.625rem] text-ink-muted'
              : 'mt-1 flex justify-between text-caption text-ink-muted'
          }
        >
          {chartData.xLabels.map((l, i) => (
            <span key={i}>{l}</span>
          ))}
        </div>
      ) : null}
    </div>
  );
}

function BarChart({
  values,
  color,
  width,
  height,
  preserveAspectRatio,
  className,
}: {
  values: number[];
  color: string;
  width: number;
  height: number;
  preserveAspectRatio: string;
  className: string;
}): JSX.Element {
  const max = Math.max(...values, 1);
  const n = Math.max(values.length, 1);
  // Adaptive gap keeps bars positive-width even with many readings.
  const gap = n > 40 ? 1 : n > 20 ? 2 : n > 12 ? 4 : 8;
  const bw = Math.max((width - gap * (n - 1)) / n, 1);
  const rx = Math.min(3, bw / 2);

  return (
    <svg
      viewBox={`0 0 ${width} ${height}`}
      preserveAspectRatio={preserveAspectRatio}
      className={className}
      role="img"
      aria-label="Usage chart"
    >
      {values.map((v, i) => {
        const h = Math.max((v / max) * (height - 4), 2);
        return (
          <rect
            key={i}
            x={i * (bw + gap)}
            y={height - h}
            width={bw}
            height={h}
            rx={rx}
            fill={color}
          />
        );
      })}
    </svg>
  );
}

/** Smooth (Catmull-Rom → bézier) path through points for a rounded line. */
function smoothLinePath(pts: Array<{ x: number; y: number }>): string {
  const first = pts[0];
  if (!first) return '';
  if (pts.length === 1) return `M ${first.x.toFixed(1)} ${first.y.toFixed(1)}`;
  const t = 0.18; // tension
  let d = `M ${first.x.toFixed(2)} ${first.y.toFixed(2)}`;
  for (let i = 0; i < pts.length - 1; i++) {
    const p0 = pts[i === 0 ? 0 : i - 1]!;
    const p1 = pts[i]!;
    const p2 = pts[i + 1]!;
    const p3 = pts[i + 2 < pts.length ? i + 2 : i + 1]!;
    const c1x = p1.x + (p2.x - p0.x) * t;
    const c1y = p1.y + (p2.y - p0.y) * t;
    const c2x = p2.x - (p3.x - p1.x) * t;
    const c2y = p2.y - (p3.y - p1.y) * t;
    d += ` C ${c1x.toFixed(2)} ${c1y.toFixed(2)}, ${c2x.toFixed(2)} ${c2y.toFixed(2)}, ${p2.x.toFixed(2)} ${p2.y.toFixed(2)}`;
  }
  return d;
}

function AreaChart({
  values,
  color,
  gradId,
  width,
  height,
  preserveAspectRatio,
  className,
  smooth = false,
}: {
  values: number[];
  color: string;
  gradId: string;
  width: number;
  height: number;
  preserveAspectRatio: string;
  className: string;
  smooth?: boolean;
}): JSX.Element {
  const max = Math.max(...values, 1);
  const stepX = width / Math.max(values.length - 1, 1);
  const yOf = (v: number) => height - (v / max) * (height - 6) - 3;
  const pts = values.map((v, i) => ({ x: i * stepX, y: yOf(v) }));
  const line = smooth
    ? smoothLinePath(pts)
    : pts.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x.toFixed(1)} ${p.y.toFixed(1)}`).join(' ');
  const area = `${line} L ${width} ${height} L 0 ${height} Z`;

  return (
    <svg
      viewBox={`0 0 ${width} ${height}`}
      preserveAspectRatio={preserveAspectRatio}
      className={className}
      role="img"
      aria-label="Usage chart"
    >
      <defs>
        <linearGradient id={gradId} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity="0.28" />
          <stop offset="100%" stopColor={color} stopOpacity="0" />
        </linearGradient>
      </defs>
      <path d={area} fill={`url(#${gradId})`} />
      <path
        d={line}
        fill="none"
        stroke={color}
        strokeWidth={2}
        strokeLinejoin="round"
        strokeLinecap="round"
      />
    </svg>
  );
}

function TimeSeriesChart({
  points,
  variant,
  color,
  gradId,
  width,
  height,
  preserveAspectRatio,
  className,
}: {
  points: Array<{ at: number; value: number; estimate?: boolean }>;
  variant: UtilityChartKind;
  color: string;
  gradId: string;
  width: number;
  height: number;
  preserveAspectRatio: string;
  className: string;
}): JSX.Element {
  const first = points[0];
  const last = points[points.length - 1];
  if (!first || !last) {
    return (
      <div className="flex h-44 w-full items-center justify-center text-body-sm text-ink-muted">
        No readings yet.
      </div>
    );
  }

  // Honor the bar variant regardless of reading count (BarChart adapts the bar
  // width/gap), so electricity is bars in both the widget and the detail view.
  if (variant === 'bar') {
    const values = points.map((p) => p.value);
    const firstLabel = new Date(first.at).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
    const lastLabel = new Date(last.at).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
    return (
      <div className="w-full">
        <BarChart
          values={values}
          color={color}
          width={width}
          height={height}
          preserveAspectRatio={preserveAspectRatio}
          className={className}
        />
        <div className="mt-1 flex justify-between text-caption text-ink-muted">
          <span>{firstLabel}</span>
          <span>{lastLabel}</span>
        </div>
      </div>
    );
  }

  const minX = first.at;
  const maxX = last.at;
  const minY = points.reduce((m, p) => Math.min(m, p.value), first.value);
  const maxY = points.reduce((m, p) => Math.max(m, p.value), first.value);
  const xRange = Math.max(maxX - minX, 1);
  const yRange = Math.max(maxY - minY, 1);

  const padL = 6;
  const padR = 6;
  const padT = 8;
  const padB = 18;

  const x = (t: number) => padL + ((t - minX) / xRange) * (width - padL - padR);
  const y = (v: number) => padT + (1 - (v - minY) / yRange) * (height - padT - padB);

  const linePts = points.map((p) => ({ x: x(p.at), y: y(p.value) }));
  const path =
    variant === 'line'
      ? smoothLinePath(linePts)
      : linePts.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x.toFixed(2)} ${p.y.toFixed(2)}`).join(' ');
  const areaPath = `${path} L ${x(last.at).toFixed(2)} ${(height - padB).toFixed(2)} L ${x(first.at).toFixed(2)} ${(height - padB).toFixed(2)} Z`;

  const firstLabel = new Date(minX).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  const lastLabel = new Date(maxX).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });

  return (
    <div className="w-full">
      <svg
        viewBox={`0 0 ${width} ${height}`}
        preserveAspectRatio={preserveAspectRatio}
        className={className}
        role="img"
        aria-label="Usage chart"
      >
        <defs>
          <linearGradient id={gradId} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={color} stopOpacity="0.28" />
            <stop offset="100%" stopColor={color} stopOpacity="0" />
          </linearGradient>
        </defs>
        <path d={areaPath} fill={`url(#${gradId})`} />
        <path d={path} fill="none" stroke={color} strokeWidth={1.75} strokeLinejoin="round" strokeLinecap="round" />
        {points.map((p, i) => (
          <circle
            key={i}
            cx={x(p.at).toFixed(2)}
            cy={y(p.value).toFixed(2)}
            r={2}
            fill={color}
            opacity={p.estimate ? 0.4 : 1}
          />
        ))}
      </svg>
      <div className="mt-1 flex justify-between text-caption text-ink-muted">
        <span>{firstLabel}</span>
        <span>{lastLabel}</span>
      </div>
    </div>
  );
}
